import { useRef, useEffect, useCallback } from "react";
import { Map as MLMap, Marker, Source, Layer, FullscreenControl, AttributionControl } from "react-map-gl/maplibre";
import maplibregl from "maplibre-gl";
import polyline from "@mapbox/polyline";
import "maplibre-gl/dist/maplibre-gl.css";
import ApiService from "../ApiService";
import ImageWithToken from "./ImageWithToken";
import { applyMapStyleTransform } from "../utils/mapStyleTransform";
import "../styles/Map.css";

const MAP_STYLE_URL = "https://tiles.openfreemap.org/styles/liberty";

// ─── Clustering ───────────────────────────────────────────────────────────────

const BASE_DEGREES = 0.01;
const BASE_ZOOM = 14;
const MAX_CLUSTER_ZOOM = 17;

function computeClusters(places, zoom) {
  if (zoom >= MAX_CLUSTER_ZOOM + 1) {
    return { clusters: [], singles: places };
  }

  const gridSize = BASE_DEGREES / Math.pow(2, zoom - BASE_ZOOM);

  const cells = new Map();
  for (const place of places) {
    if (!place.location) continue;
    const lat = place.location.latitude;
    const lng = place.location.longitude;
    const key = `${Math.floor(lat / gridSize)}_${Math.floor(lng / gridSize)}`;
    if (!cells.has(key)) cells.set(key, []);
    cells.get(key).push(place);
  }

  const clusters = [];
  const singles = [];

  for (const group of cells.values()) {
    if (group.length === 1) {
      singles.push(group[0]);
    } else {
      const lat = group.reduce((s, p) => s + p.location.latitude, 0) / group.length;
      const lng = group.reduce((s, p) => s + p.location.longitude, 0) / group.length;
      clusters.push({ lat, lng, count: group.length, places: group });
    }
  }

  return { clusters, singles };
}

// ─── Markers ─────────────────────────────────────────────────────────────────

const PhotoMarker = ({ place, user, onMarkerClick, routeNumber }) => {
  if (!place.location) return null;

  return (
    <Marker
      longitude={place.location.longitude}
      latitude={place.location.latitude}
      anchor="center"
      onClick={(e) => {
        e.originalEvent.stopPropagation();
        onMarkerClick(place);
      }}
    >
      <div className="advanced-custom-marker">
        <div className="advanced-custom-marker-photo">
          <ImageWithToken
            className="advanced-custom-marker-photo-img"
            imageUrl={ApiService.create_place_photo_url(place)}
            user={user}
            alt={place.placeName}
          />
          {routeNumber != null && (
            <div className="marker-route-number">{routeNumber}</div>
          )}
        </div>
      </div>
    </Marker>
  );
};

const ClusterMarker = ({ cluster, onClusterClick }) => (
  <Marker
    longitude={cluster.lng}
    latitude={cluster.lat}
    anchor="center"
    onClick={(e) => {
      e.originalEvent.stopPropagation();
      onClusterClick(cluster);
    }}
  >
    <div className="cluster-marker">{cluster.count}</div>
  </Marker>
);

// ─── Polyline ────────────────────────────────────────────────────────────────

function PolylineFromEncoded({ encoded }) {
  if (!encoded) return null;

  // @mapbox/polyline.decode returns [[lat, lng], ...]; GeoJSON wants [lng, lat].
  const coords = polyline.decode(encoded).map(([lat, lng]) => [lng, lat]);
  if (!coords.length) return null;

  const geojson = {
    type: "Feature",
    properties: {},
    geometry: { type: "LineString", coordinates: coords },
  };

  return (
    <Source id="route-line" type="geojson" data={geojson}>
      <Layer
        id="route-line-layer"
        type="line"
        paint={{
          "line-color": "#6C63FF",
          "line-width": 4,
          "line-dasharray": [0.3, 1.5],
        }}
        layout={{ "line-cap": "round", "line-join": "round" }}
      />
    </Source>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function GoogleMap({
  defaultCenter, defaultZoom, places, polyline: encodedPolyline, userLocation, userHeading, user,
  onCenterChanged, center, onMarkerClick, selectedList, routeOrigin, routeDest, zoom,
  onRequestOrientationPermission,
}) {
  const mapRef = useRef(null);
  const orientationRequestedRef = useRef(false);

  const handleMapClick = useCallback(() => {
    if (orientationRequestedRef.current) return;
    orientationRequestedRef.current = true;
    onRequestOrientationPermission?.();
  }, [onRequestOrientationPermission]);

  useEffect(() => {
    if (!center || !mapRef.current) return;
    const map = mapRef.current.getMap?.() ?? mapRef.current;
    map.flyTo({ center: [center.lng, center.lat], duration: 500, essential: true });
  }, [center]);

  const handleMapLoad = useCallback((e) => {
    applyMapStyleTransform(e.target);
  }, []);

  const handleMoveEnd = useCallback((evt) => {
    const map = evt.target;
    if (!map) return;
    onCenterChanged?.({ map });
  }, [onCenterChanged]);

  const handleClusterClick = useCallback((cluster) => {
    const map = mapRef.current?.getMap?.() ?? mapRef.current;
    if (!map) return;
    map.easeTo({
      center: [cluster.lng, cluster.lat],
      zoom: Math.min(map.getZoom() + 2, 18),
      duration: 500,
    });
  }, []);

  if (!defaultCenter) return null;

  // Split route stops (never clustered) from regular places.
  const routePlaces = places.filter(p => selectedList.has(p.reference));
  const regularPlaces = places.filter(p => !selectedList.has(p.reference));
  const { clusters, singles } = computeClusters(regularPlaces, zoom ?? defaultZoom);

  return (
    <div className="map-section">
      <MLMap
        ref={mapRef}
        mapLib={maplibregl}
        initialViewState={{
          longitude: defaultCenter.lng,
          latitude: defaultCenter.lat,
          zoom: defaultZoom,
        }}
        minZoom={10}
        maxZoom={18}
        style={{ width: "100%", height: "100%" }}
        mapStyle={MAP_STYLE_URL}
        attributionControl={false}
        onLoad={handleMapLoad}
        onClick={handleMapClick}
        onMoveEnd={handleMoveEnd}
      >
        <AttributionControl compact={true} />
        <FullscreenControl position="top-right" />

        {/* Route stop markers — always individual, with route number */}
        {routePlaces.map((place, index) => (
          <PhotoMarker
            key={place.reference}
            place={place}
            user={user}
            onMarkerClick={onMarkerClick}
            routeNumber={index + 1}
          />
        ))}

        {/* Single (non-clustered) regular markers */}
        {singles.map(place => (
          <PhotoMarker
            key={place.reference}
            place={place}
            user={user}
            onMarkerClick={onMarkerClick}
            routeNumber={null}
          />
        ))}

        {/* Cluster markers */}
        {clusters.map((cluster, i) => (
          <ClusterMarker
            key={`cluster_${i}`}
            cluster={cluster}
            onClusterClick={handleClusterClick}
          />
        ))}

        {/* User location */}
        {userLocation && (
          <Marker
            key="UserMarker"
            longitude={userLocation.lng}
            latitude={userLocation.lat}
            anchor="center"
          >
            <div className="user-location-marker">
              {userHeading != null && (
                <div
                  className="user-location-cone"
                  style={{ transform: `rotate(${userHeading}deg)` }}
                />
              )}
              <div className="user-location-dot" />
            </div>
          </Marker>
        )}

        {/* Route origin */}
        {routeOrigin && (
          <Marker
            key="RouteOrigin"
            longitude={routeOrigin.lng}
            latitude={routeOrigin.lat}
            anchor="bottom"
          >
            <div className="route-endpoint-marker route-endpoint-marker--origin" />
          </Marker>
        )}

        {/* Route destination */}
        {routeDest && (
          <Marker
            key="RouteDest"
            longitude={routeDest.lng}
            latitude={routeDest.lat}
            anchor="bottom"
          >
            <div className="route-endpoint-marker route-endpoint-marker--dest" />
          </Marker>
        )}

        <PolylineFromEncoded encoded={encodedPolyline} />
      </MLMap>
    </div>
  );
}
